import { CaretLeftIcon, ClockIcon, HandCoinsIcon } from '@phosphor-icons/react'
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { Button } from '@/components/business/visitor-parking/button'
import { ButtonIcon } from '@/components/business/visitor-parking/button-icon'
import { Card } from '@/components/business/visitor-parking/card'
import { DetailsCard } from '@/components/business/visitor-parking/details-card'
import { HelpSection } from '@/components/business/visitor-parking/help-section'
import { Icon } from '@/components/ui/icon'
import { useVisitorParkingStore } from '@/pages/visitor-parking/visitor-parking.store'
import {
  formatCurrency,
  formatDuration,
  getFullPaymentSuccessReturnUrl,
  getRelativePaymentSuccessReturnUrl,
} from '@/pages/visitor-parking/visitor-parking.utils'
import { VisitorParkingFormLayout } from '@/pages/visitor-parking/visitor-parking-form-layout'

import {
  STRIPE_PAYMENT_ELEMENT_OPTIONS,
  VISITOR_PARKING_TOKEN_URL_KEY,
} from '../visitor-parking.constants'
import * as S from './extend-parking-payment.styled'

interface ExtendParkingPaymentProps {
  onBack: () => void
  onPaymentError: () => void
}

export const ExtendParkingPayment = ({ onBack, onPaymentError }: ExtendParkingPaymentProps) => {
  const { t } = useTranslation(['visitor-parking'])
  const { duration, paymentSession, visitor } = useVisitorParkingStore()
  const stripe = useStripe()
  const elements = useElements()
  const form = useForm()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get(VISITOR_PARKING_TOKEN_URL_KEY) ?? ''

  if (!paymentSession) return null

  const onSubmit = async () => {
    if (!stripe || !elements) {
      return
    }

    const { error: validationError } = await elements.submit()
    if (validationError) {
      return
    }

    try {
      const { error } = await stripe.confirmPayment({
        clientSecret: paymentSession.clientSecret ?? '',
        confirmParams: {
          return_url: getFullPaymentSuccessReturnUrl(paymentSession.ticketNumber, token),
        },
        elements,
        redirect: 'if_required',
      })
      if (error) {
        throw error
      }
      navigate(getRelativePaymentSuccessReturnUrl(paymentSession.ticketNumber, token))
    } catch {
      onPaymentError()
    }
  }

  return (
    <VisitorParkingFormLayout
      footer={
        <>
          <ButtonIcon type="button" onClick={onBack}>
            <Icon icon={CaretLeftIcon} />
          </ButtonIcon>
          <Button
            disabled={!form.formState.isValid}
            loading={form.formState.isSubmitting}
            type="submit"
            onClick={form.handleSubmit(onSubmit)}
          >
            {t('visitor-parking:extendParkingPayment.pay', {
              amount: formatCurrency(paymentSession.amount),
            })}
          </Button>
        </>
      }
    >
      <Card>
        <S.Heading>{t('visitor-parking:extendParkingPayment.serviceCostHeading')}</S.Heading>
        <S.CardsContainer>
          <DetailsCard
            icon={ClockIcon}
            title={t('visitor-parking:extendParkingPayment.time')}
            value={formatDuration(duration, t)}
          />
          <DetailsCard
            icon={HandCoinsIcon}
            title={t('visitor-parking:extendParkingPayment.fee')}
            value={formatCurrency(paymentSession.amount)}
          />
        </S.CardsContainer>
      </Card>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Card>
          <S.Heading>{t('visitor-parking:extendParkingPayment.paymentMethodHeading')}</S.Heading>
          <PaymentElement
            options={{
              ...STRIPE_PAYMENT_ELEMENT_OPTIONS,
              // Note: Add default values for saving card information
              defaultValues: {
                billingDetails: {
                  email: visitor?.email,
                  name: visitor?.fullName,
                  phone: visitor?.phone,
                },
              },
            }}
          />
        </Card>
      </form>
      <HelpSection />
    </VisitorParkingFormLayout>
  )
}
